"use client";

import React, { ReactNode } from "react";
import ErrorBoundary from "./ErrorBoundary";

interface FeatureErrorBoundaryProps {
  children: ReactNode;
  featureName: string;
  fallbackComponent?: ReactNode;
  showRetry?: boolean;
  onRetry?: () => void;
}

/**
 * FeatureErrorBoundary provides error handling for specific features or sections.
 * It isolates errors to improve user experience and debugging.
 */
const FeatureErrorBoundary: React.FC<FeatureErrorBoundaryProps> = ({
  children,
  featureName,
  fallbackComponent,
  showRetry = true,
  onRetry,
}) => {
  const handleError = (error: Error) => {
    // Log feature-specific errors
    console.error(`Error in feature "${featureName}":`, error);

    // You can send feature-specific error analytics here
    // Example: analytics.track('Feature Error', { feature: featureName, error: error.message });
  };

  const defaultFallback = (
    <div className="border border-red-200 bg-red-50 rounded p-6 m-4">
      <div className="flex items-start">
        <div className="flex-shrink-0">
          <svg
            className="h-6 w-6 text-red-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-red-800">
            {featureName} Unavailable
          </h3>
          <p className="mt-1 text-sm text-red-700">
            This feature is temporarily unavailable. Please try again later.
          </p>
          {showRetry && (
            <div className="mt-4">
              <button
                onClick={() => {
                  if (onRetry) {
                    onRetry();
                  } else {
                    window.location.reload();
                  }
                }}
                className="bg-red-100 hover:bg-red-200 text-red-800 font-medium py-2 px-4 rounded text-sm transition-colors"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <ErrorBoundary
      fallback={fallbackComponent || defaultFallback}
      onError={handleError}
    >
      {children}
    </ErrorBoundary>
  );
};

export default FeatureErrorBoundary;
